/* ============================================================================
 * 信息架构：工作台 + 7 业务域
 * 继承自 V2（该 IA 已经过二轮修订，禁用了"作战/兵部/三省六部"类隐喻命名，
 * 并对标 Kinaxis/o9/SAP IBP 补齐了需求计划/ATP/库存优化三模块）。
 * ========================================================================== */
import type { IconName } from '@/components/Icon';

export interface NavLeaf {
  route: string;
  name: string;
  icon: IconName;
  /** 命令面板的额外检索关键词（F-53 / F-59）*/
  keywords?: string[];
}

export interface NavGroup {
  group: string;
  name: string;
  icon: IconName;
  children: NavLeaf[];
}

export type NavNode = (NavLeaf & { group?: undefined }) | NavGroup;

export const NAV: NavNode[] = [
  { route: 'home', name: '工作台', icon: 'home', keywords: ['首页', '待办', 'dashboard'] },
  {
    group: 'insight', name: '经营洞察', icon: 'chart', children: [
      { route: 'overview', name: '经营驾驶舱', icon: 'map', keywords: ['地图', '全局', 'cockpit'] },
      { route: 'plant', name: '工厂透视', icon: 'factory', keywords: ['基地', '下钻', '工序流'] },
      { route: 'reports', name: '经营报表', icon: 'chart', keywords: ['利润', '归因', '8因子'] },
      { route: 'benchmark', name: '对标分析', icon: 'target', keywords: ['竞品', '标杆', '雷达'] },
    ],
  },
  {
    group: 'planning', name: '计划中心', icon: 'calendar', children: [
      { route: 'demand', name: '需求计划', icon: 'gauge', keywords: ['预测', 'forecast', '置信区间'] },
      { route: 'sop', name: 'S&OP 月度规划', icon: 'calendar', keywords: ['产销协同', '月度', '基线'] },
      { route: 'plan', name: '主计划管理', icon: 'flow', keywords: ['主计划', 'MPS', '滚动'] },
    ],
  },
  {
    group: 'scheduling', name: '排程中心', icon: 'factory', children: [
      { route: 'operational', name: '智能派单', icon: 'flow', keywords: ['运营级', '派单', 'CP-SAT', '求解'] },
      { route: 'factory', name: '车间排程', icon: 'factory', keywords: ['甘特', '工序', 'FJSP', 'gantt'] },
      { route: 'approval', name: '排产审批', icon: 'stamp', keywords: ['审批', '状态机', '下发'] },
      { route: 'emergency', name: '紧急插单', icon: 'bolt', keywords: ['插单', '冻结窗口', '加急'] },
      { route: 'whatif', name: '情景模拟', icon: 'beaker', keywords: ['what-if', '沙盘', '推演', '情景'] },
      { route: 'capacity', name: '产能衔接镜像', icon: 'layers', keywords: ['产能', '对账', '偏移'] },
      { route: 'rules', name: '排程规则', icon: 'sliders', keywords: ['优先级', '权重', 'L1', 'L4'] },
    ],
  },
  {
    group: 'fulfillment', name: '订单履约', icon: 'orders', children: [
      { route: 'orders', name: '销售订单', icon: 'orders', keywords: ['订单', '台账', 'SO'] },
      { route: 'atp', name: '交期承诺 ATP', icon: 'check', keywords: ['ATP', 'CTP', '承诺', '询单'] },
      { route: 'customer', name: '客户协同', icon: 'users', keywords: ['客户', '健康度', '份额'] },
    ],
  },
  {
    group: 'supply', name: '供应保障', icon: 'box', children: [
      { route: 'material', name: '物料齐套', icon: 'box', keywords: ['齐套', 'BOM', '缺料', '采购'] },
      { route: 'inventory', name: '库存优化', icon: 'db', keywords: ['库存', '安全库存', '周转'] },
      { route: 'resources', name: '设备资源', icon: 'wrench', keywords: ['设备', '检修', 'OEE', '工作中心'] },
    ],
  },
  {
    group: 'monitor', name: '运营监控', icon: 'radar', children: [
      { route: 'tower', name: '供应链控制塔', icon: 'radar', keywords: ['波动', '控制塔', '预警', 'L1L2L3'] },
      { route: 'trace', name: '决策追溯', icon: 'dna', keywords: ['追溯', '谱系', 'task_trace', '审计'] },
    ],
  },
  {
    group: 'admin', name: '系统设置', icon: 'shield', children: [
      { route: 'master', name: '主数据', icon: 'db', keywords: ['主数据', '基地', '产品', '物料'] },
      { route: 'integration', name: '集成中台', icon: 'plug', keywords: ['集成', 'NCC', 'MES', '连接器'] },
      { route: 'permissions', name: '权限管理', icon: 'shield', keywords: ['RBAC', '角色', '权限矩阵'] },
      { route: 'aiconfig', name: 'AI 助手配置', icon: 'bot', keywords: ['AI', '副驾', '模型', '供应商'] },
    ],
  },
];

export interface FlatNav extends NavLeaf {
  groupKey: string | null;
  groupName: string | null;
}

export const FLAT_NAV: FlatNav[] = NAV.flatMap((n): FlatNav[] =>
  'group' in n && n.group
    ? n.children.map((c) => ({ ...c, groupKey: n.group, groupName: n.name }))
    : [{ ...(n as NavLeaf), groupKey: null, groupName: null }],
);

export const findNav = (route: string) => FLAT_NAV.find((x) => x.route === route);
